import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Flag } from "lucide-react";
import { Reveal } from "./Reveal";
import { post } from "@/lib/api";

const EMPTY = { name: "", age: "", gender: "", address: "", email: "", mobile: "", village: "", constituency: "", voter_id: "", occupation: "" };

export const Membership = () => {
  const [form, setForm] = useState(EMPTY);
  const [declaration, setDeclaration] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [attempt, setAttempt] = useState(0);
  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (errors[k]) setErrors({ ...errors, [k]: false });
  };
  const errCls = (k) => (errors[k] ? ` field-error bounce-${attempt % 2}` : "");

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    const ageN = parseInt(form.age, 10);
    if (form.name.trim().length < 2) errs.name = true;
    if (!ageN || ageN < 18 || ageN > 120) errs.age = true;
    if (!form.gender) errs.gender = true;
    if (form.address.trim().length < 5) errs.address = true;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = true;
    if (!/^[0-9+\-\s]{7,15}$/.test(form.mobile.trim())) errs.mobile = true;
    if (form.village.trim().length < 2) errs.village = true;
    if (form.constituency.trim().length < 2) errs.constituency = true;
    if (!declaration) errs.declaration = true;
    if (Object.values(errs).some(Boolean)) {
      setErrors(errs);
      setAttempt((a) => a + 1);
      toast.error(errs.declaration && Object.keys(errs).length === 1
        ? "Please accept the declaration to proceed."
        : "Please fix the highlighted fields.");
      return;
    }
    setErrors({});
    const payload = { ...form, age: ageN, declaration: true };
    setLoading(true);
    try {
      await post("/membership", payload);
      toast.success("Thank you for registering! We will get in touch with you soon for the membership process.", { duration: 8000 });
      setForm(EMPTY);
      setDeclaration(false);
    } catch {
      toast.error("Could not submit. Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="membership" className="relative py-28 bg-yellow-400 text-black overflow-hidden" data-testid="membership-section">
      <span className="absolute -top-8 right-0 font-display font-black uppercase text-[16vw] leading-none text-black/5 select-none pointer-events-none">
        JOIN US
      </span>
      <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-[1fr_1.2fr] gap-14 relative">
        <Reveal>
          <p className="font-mono-rgp text-[11px] uppercase tracking-[0.28em] text-black/60 mb-4">// Membership</p>
          <h2 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            Join the Movement.<br />Shape Goa's Future.
          </h2>
          <p className="mt-6 text-black/80 leading-relaxed max-w-md font-medium">
            Goa's future belongs to all of us.
          </p>
          <p className="mt-3 text-black/70 leading-relaxed max-w-md">
            The Revolutionary Goans Party (RGP) brings together people who care about
            Goa and want to participate in shaping its future.
          </p>
          <p className="mt-3 text-black/70 leading-relaxed max-w-md">
            Become a member and be part of a growing grassroots organisation where
            every voice, every idea and every contribution matters.
          </p>
          <div className="mt-8 inline-flex flex-col gap-1 bg-black text-yellow-400 px-5 py-4" data-testid="membership-fee-badge">
            <span className="flex items-center gap-3 font-display font-bold uppercase text-lg">
              <Flag size={20} /> Membership Fee: Rs 5/- (Rupees Five only)
            </span>
            <span className="text-xs text-yellow-400/70">Membership is for one year.</span>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="bg-black/95 p-8 lg:p-10 grid sm:grid-cols-2 gap-4 shadow-[0_30px_80px_rgba(0,0,0,0.35)]" data-testid="membership-form">
            <h3 className="sm:col-span-2 font-display font-bold uppercase text-2xl text-white text-center bg-yellow-400/10 py-3">
              Member Registration Form
            </h3>
            <input data-testid="membership-form-input-name" className={`rgp-input sm:col-span-2${errCls("name")}`} placeholder="Name (full name) *" value={form.name} onChange={set("name")} />
            <input data-testid="membership-form-input-age" className={`rgp-input${errCls("age")}`} type="number" min="18" max="120" placeholder="Age *" value={form.age} onChange={set("age")} />
            <select data-testid="membership-form-select-gender" className={`rgp-input${errCls("gender")}`} value={form.gender} onChange={set("gender")}>
              <option value="" className="bg-black">Gender *</option>
              {["Male", "Female", "Other"].map((g) => <option key={g} value={g} className="bg-black">{g}</option>)}
            </select>
            <textarea data-testid="membership-form-input-address" className={`rgp-input sm:col-span-2 min-h-[70px]${errCls("address")}`} placeholder="Address *" value={form.address} onChange={set("address")} />
            <input data-testid="membership-form-input-email" className={`rgp-input${errCls("email")}`} type="email" placeholder="Email *" value={form.email} onChange={set("email")} />
            <input data-testid="membership-form-input-mobile" className={`rgp-input${errCls("mobile")}`} placeholder="Mobile No. *" value={form.mobile} onChange={set("mobile")} />
            <input data-testid="membership-form-input-village" className={`rgp-input${errCls("village")}`} placeholder="Village *" value={form.village} onChange={set("village")} />
            <input data-testid="membership-form-input-constituency" className={`rgp-input${errCls("constituency")}`} placeholder="Constituency *" value={form.constituency} onChange={set("constituency")} />
            <input data-testid="membership-form-input-voter-id" className="rgp-input sm:col-span-2" placeholder="Voter ID / Election Card No." value={form.voter_id} onChange={set("voter_id")} />
            <input data-testid="membership-form-input-occupation" className="rgp-input sm:col-span-2" placeholder="Occupation" value={form.occupation} onChange={set("occupation")} />
            <label className={`sm:col-span-2 flex items-start gap-3 cursor-pointer text-xs leading-relaxed ${errors.declaration ? `field-error-text bounce-${attempt % 2}` : "text-zinc-400"}`}>
              <input
                data-testid="membership-form-declaration-checkbox"
                type="checkbox"
                checked={declaration}
                onChange={(e) => {
                  setDeclaration(e.target.checked);
                  if (errors.declaration) setErrors({ ...errors, declaration: false });
                }}
                className="mt-0.5 h-4 w-4 accent-yellow-400 shrink-0"
              />
              <span>
                I hereby declare that I am a citizen of India, affirm my faith in
                Schedule II of the Constitution of the Party, and certify that I am not
                a member of any other political party. I further undertake to abide by
                the Constitution, rules, regulations, and discipline of the Party.
              </span>
            </label>
            <button data-testid="membership-form-submit-button" disabled={loading} className="btn-yellow sm:col-span-2 justify-center disabled:opacity-60">
              {loading ? <Loader2 className="animate-spin" size={18} /> : null}
              {loading ? "Processing..." : "Register Now"}
            </button>
            <p className="sm:col-span-2 text-[11px] text-zinc-500 leading-relaxed text-center">
              Membership Fee Rs: 5/- (Rupees Five only) · Membership is for one year.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
